import { useState } from "react"
import {
    Typography,
    Button,
    Space,
    Card,
    Select,
    Table,
    Input,
    message,
    Modal,
    Form,
    List,
    Tag,
} from "antd"

const { Title, Text } = Typography
const { Option } = Select

// Helper for Ant Design message
const showMessage = (type, content) => {
    message[type](content)
}

const AccessRoles = () => {
    const [users, setUsers] = useState([
        {
            key: "1",
            name: "Ellie Johnson",
            email: "ellie@company.com",
            role: "admin",
            lastActive: "June 14, 2025 • 8:42 PM",
        },
        {
            key: "2",
            name: "John Doe",
            email: "john@company.com",
            role: "contributor",
            lastActive: "June 13, 2025 • 10:15 AM",
        },
        { key: "3", name: "Jane Smith", email: "jane@company.com", role: "viewer", lastActive: "June 12, 2025 • 3:00 PM" },
        {
            key: "4",
            name: "Invited User",
            email: "new@company.com",
            role: "contributor",
            lastActive: "Pending",
            invited: true,
        },
    ])
    const [inviteForm] = Form.useForm()

    const handleRoleChange = (key, newRole) => {
        setUsers(users.map((user) => (user.key === key ? { ...user, role: newRole } : user)))
    }

    const handleRemoveUser = (key) => {
        Modal.confirm({
            title: "Confirm Removal",
            content: "Are you sure you want to remove this user?",
            onOk: () => {
                setUsers(users.filter((user) => user.key !== key))
                showMessage("success", "User removed successfully.")
            },
        })
    }

    const handleInviteUser = (values) => {
        const newUser = {
            key: String(users.length + 1),
            name: values.email.split("@")[0], // Simple name from email
            email: values.email,
            role: values.role,
            lastActive: "Pending",
            invited: true,
        }
        setUsers([...users, newUser])
        inviteForm.resetFields()
        showMessage("success", "Invitation sent successfully.")
    }

    const columns = [
        {
            title: "Name",
            dataIndex: "name",
            key: "name",
            render: (text, record) => (
                <span>
                    {text} {record.invited && <Tag color="blue">Invited</Tag>}
                </span>
            ),
        },
        { title: "Email", dataIndex: "email", key: "email" },
        {
            title: "Role",
            dataIndex: "role",
            key: "role",
            render: (text, record) => (
                <Select value={text} onChange={(value) => handleRoleChange(record.key, value)} className="w-32">
                    <Option value="admin">Admin</Option>
                    <Option value="contributor">Contributor</Option>
                    <Option value="viewer">Viewer</Option>
                </Select>
            ),
        },
        { title: "Last Active", dataIndex: "lastActive", key: "lastActive" },
        {
            title: "Actions",
            key: "actions",
            render: (text, record) => (
                <Space size="middle">
                    {record.invited ? (
                        <Button type="link" onClick={() => showMessage("info", `Reinviting ${record.email}`)}>
                            Reinvite
                        </Button>
                    ) : (
                        <Button type="link" danger onClick={() => handleRemoveUser(record.key)}>
                            Remove
                        </Button>
                    )}
                </Space>
            ),
        },
    ]

    const currentSessions = [
        { id: "1", device: "MacBook Pro", location: "San Francisco", ip: "192.0.2.1", lastUsed: "Current session (You)" },
        { id: "2", device: "Pixel 8", location: "New York", ip: "172.16.0.5", lastUsed: "June 12, 2025 • 10:00 AM" },
    ]

    const handleLogoutAllOtherSessions = () => {
        Modal.confirm({
            title: "Confirm Logout",
            content: "Are you sure you want to log out of all other sessions?",
            onOk: () => {
                showMessage("success", "Logged out of all other sessions.")
                // In a real app, this would send a request to invalidate other sessions
            },
        })
    }

    return (
        <Card className="mb-6">
            <div className="mb-4">
                <Title level={4}>Access & Roles</Title>
                <Text type="secondary">Manage team members, permissions, and security settings.</Text>
            </div>

            <Title level={5} className="mb-4">
                User List
            </Title>
            <Table dataSource={users} columns={columns} pagination={{ pageSize: 5 }} className="mb-6" />

            <Title level={5} className="mb-4">
                Invite User
            </Title>
            <Form form={inviteForm} layout="inline" onFinish={handleInviteUser} className="mb-6">
                <Form.Item name="email" rules={[{ required: true, type: "email", message: "Please enter a valid email!" }]}>
                    <Input placeholder="Email address" />
                </Form.Item>
                <Form.Item name="role" initialValue="contributor">
                    <Select className="w-32">
                        <Option value="admin">Admin</Option>
                        <Option value="contributor">Contributor</Option>
                        <Option value="viewer">Viewer</Option>
                    </Select>
                </Form.Item>
                <Form.Item>
                    <Button type="primary" htmlType="submit">
                        Send Invite
                    </Button>
                </Form.Item>
            </Form>

            <Title level={5} className="mb-4">
                Role Definitions
            </Title>
            <List
                dataSource={[
                    { role: "Admin", description: "Full access, can change settings, manage users, and execute tasks" },
                    { role: "Contributor", description: "Can view and suggest changes but not execute" },
                    { role: "Viewer", description: "Read-only access" },
                ]}
                renderItem={(item) => (
                    <List.Item>
                        <List.Item.Meta title={<Text strong>{item.role}</Text>} description={item.description} />
                    </List.Item>
                )}
                className="mb-6"
            />

            <Title level={5} className="mb-4">
                Current Sessions
            </Title>
            <List
                dataSource={currentSessions}
                renderItem={(item) => (
                    <List.Item>
                        <div className="flex justify-between w-full items-center">
                            <Text>
                                {item.device} ({item.location}) - {item.lastUsed}
                            </Text>
                            {item.lastUsed !== "Current session (You)" && (
                                <Button type="link" danger onClick={() => showMessage("info", `Removing session for ${item.device}`)}>
                                    Remove
                                </Button>
                            )}
                        </div>
                    </List.Item>
                )}
                className="mb-4"
            />
            <Button danger onClick={handleLogoutAllOtherSessions}>
                Log Out of All Other Sessions
            </Button>
        </Card>
    )
}

export default AccessRoles;